import type {
  BadgeMap,
  GroupingOption,
  ReportColumn,
  TabConfig,
} from '../../lib/types'
import type { SalesRow } from '../../api/use-sales-query'

export const ORDER_STATUS_BADGES: BadgeMap = {
  CREATED: { label: 'Erstellt', variant: 'secondary' },
  COMPLETED: { label: 'Abgeschlossen', variant: 'default' },
  CANCELLED: { label: 'Storniert', variant: 'destructive' },
  REFUNDED: { label: 'Erstattet', variant: 'outline' },
}

export const PAYMENT_STATUS_BADGES: BadgeMap = {
  PENDING: { label: 'Ausstehend', variant: 'secondary' },
  AUTHORIZED: { label: 'Autorisiert', variant: 'outline' },
  CAPTURED: { label: 'Erfasst', variant: 'default' },
  REFUNDED: { label: 'Erstattet', variant: 'outline' },
  VOIDED: { label: 'Storniert', variant: 'destructive' },
}

export const CHANNEL_BADGES: BadgeMap = {
  pos: { label: 'POS', variant: 'default' },
  mobile: { label: 'Online', variant: 'secondary' },
}

export const SPLIT_PAYMENT_BADGES: BadgeMap = {
  true: { label: 'Ja', variant: 'outline' },
}

export const SALES_COLUMNS: Array<ReportColumn<SalesRow>> = [
  { key: 'occurredAt', label: 'Zeitpunkt', type: 'datetime' },
  { key: 'channel', label: 'Kanal', type: 'badge', badgeMap: CHANNEL_BADGES },
  {
    key: 'orderStatus',
    label: 'Bestellstatus',
    type: 'badge',
    badgeMap: ORDER_STATUS_BADGES,
  },
  {
    key: 'paymentStatus',
    label: 'Zahlungsstatus',
    type: 'badge',
    badgeMap: PAYMENT_STATUS_BADGES,
  },
  { key: 'paymentMethods', label: 'Zahlungsart', type: 'text' },
  {
    key: 'splitPayment',
    label: 'Splitzahlung',
    type: 'boolean-badge',
    badgeMap: SPLIT_PAYMENT_BADGES,
    hiddenWhenFalse: true,
  },
  { key: 'totalGrossCents', label: 'Brutto', type: 'currency', summable: true },
  { key: 'receiptNo', label: 'Beleg', type: 'text' },
  { key: 'operatorId', label: 'Bediener', type: 'text' },
  { key: 'stationId', label: 'Station', type: 'text' },
  {
    key: 'id',
    label: 'ID',
    type: 'text',
    className: 'max-w-[120px] truncate font-mono text-xs',
  },
]

export const SALES_TABS: Array<TabConfig> = [
  { key: 'all', label: 'Alle' },
  { key: 'pos', label: 'POS', defaultFilters: { channel: 'pos' } },
  { key: 'online', label: 'Online', defaultFilters: { channel: 'mobile' } },
]

export const SALES_GROUPING_OPTIONS: Array<GroupingOption> = [
  { key: 'day', label: 'Tag', category: 'Zeitraum' },
  { key: 'week', label: 'Woche', category: 'Zeitraum' },
  { key: 'month', label: 'Monat', category: 'Zeitraum' },
  { key: 'quarter', label: 'Quartal', category: 'Zeitraum' },
  { key: 'year', label: 'Jahr', category: 'Zeitraum' },
  { key: 'channel', label: 'Kanal', category: 'Kanal & Station' },
  { key: 'restaurantId', label: 'Standort', category: 'Kanal & Station' },
  { key: 'operatorId', label: 'Bediener', category: 'Kanal & Station' },
  { key: 'stationId', label: 'Station', category: 'Kanal & Station' },
  { key: 'paymentMethod', label: 'Zahlungsart', category: 'Zahlung & Status' },
  { key: 'splitPayment', label: 'Splitzahlung', category: 'Zahlung & Status' },
  { key: 'orderStatus', label: 'Bestellstatus', category: 'Zahlung & Status' },
  {
    key: 'paymentStatus',
    label: 'Zahlungsstatus',
    category: 'Zahlung & Status',
  },
]

export const DATE_GROUP_KEYS = new Set([
  'year',
  'quarter',
  'month',
  'week',
  'day',
])

export const ORDER_STATUS_OPTIONS = [
  { value: 'CREATED', label: 'Erstellt' },
  { value: 'COMPLETED', label: 'Abgeschlossen' },
  { value: 'CANCELLED', label: 'Storniert' },
  { value: 'REFUNDED', label: 'Erstattet' },
]

export const PAYMENT_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Ausstehend' },
  { value: 'AUTHORIZED', label: 'Autorisiert' },
  { value: 'CAPTURED', label: 'Erfasst' },
  { value: 'REFUNDED', label: 'Erstattet' },
  { value: 'VOIDED', label: 'Storniert' },
]

export const PAYMENT_METHOD_OPTIONS = [
  { value: 'card', label: 'Karte' },
  { value: 'cash_recycler', label: 'Wechselautomat' },
  { value: 'cash_manual', label: 'Manuell bar' },
  { value: 'voucher', label: 'Gutschein' },
]
